import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import config from '@iotv/config';
import { camelToDisplay, capitalize, isNumber, roundDecimals, UserAttributeKeyDatePrecision } from '@iotv/datamodel-core';
import { isNonNaNNumber } from '@iotv/iotv-v3-types';
import { Checkbox, Typography } from '@mui/material';
import { useEffect } from 'react';
import { isInLimits, validByRegex } from './validation';
const isStringOrNumber = (v) => ['string', 'number'].includes(typeof v);
export const getViewCellRenderer = ({ viewKeyDefinition, validationMessageFn, validationMap, }) => {
    const cellRendererMap = {
        string: (p) => _jsx(GridCellString, { gridRenderCellParams: p, viewKeyDefinition, validationMessageFn, validationMap }),
        number: (p) => _jsx(GridCellNumber, { gridRenderCellParams: p, viewKeyDefinition, validationMessageFn, validationMap }),
        boolean: (p) => _jsx(_Fragment, { children: camelToDisplay(p.value?.v?.toString() || 'False') }),
        enum: (p) => (_jsx(GridCellEnumSelector, { enumeration: viewKeyDefinition.enumKV ?? {}, gridRenderCellParams: p, validationMessageFn, validationMap, viewKeyDefinition })),
        date: (p) => _jsx(GridCellDate, { gridRenderCellParams: p, viewKeyDefinition }),
        object: viewKeyDefinition.valueGetter ? (p) => _jsx(GridCellObject, { gridRenderCellParams: p, viewKeyDefinition }) : undefined,
    };
    return viewKeyDefinition.type ? cellRendererMap[viewKeyDefinition.type] : () => _jsx("div", { children: "No viewKeyDefinition.type" });
};
export const GridCellObject = ({ gridRenderCellParams, viewKeyDefinition }) => {
    const attributeValue = gridRenderCellParams.value?.v;
    let displayValue = viewKeyDefinition.valueGetter?.(attributeValue, undefined);
    if (viewKeyDefinition.valueFormatter && displayValue !== undefined) {
        displayValue = viewKeyDefinition.valueFormatter(displayValue);
    }
    return typeof displayValue === 'string' ? _jsx(Typography, { children: displayValue }) : undefined;
};
const GridCellEnumSelector = ({ enumeration, gridRenderCellParams, validationMessageFn, validationMap, viewKeyDefinition }) => {
    const debug = process.env.REACT_APP_DEBUG && false;
    let displayValue = undefined;
    const attributeValue = gridRenderCellParams.value?.v;
    debug && console.log('GridCellEnumSelector', { attributeValue, enumeration, gridRenderCellParams });
    if (isStringOrNumber(attributeValue)) {
        displayValue = Object.entries(enumeration).find(([k, v]) => v === attributeValue)?.[0];
    }
    if (displayValue) {
        displayValue = capitalize(displayValue.replace('_', ' '));
    }
    const { validationRx, pass, failsValidationText, key, required, valueFormatter, hidden } = viewKeyDefinition;
    useEffect(() => {
        if (typeof displayValue === 'string') {
            if (validationRx) {
                if (validByRegex(displayValue, validationRx, pass)) {
                    debug && console.log('valid by regex', { displayValue, validationRx });
                    if (validationMap[key]) {
                        validationMessageFn({ [key]: undefined });
                    }
                }
                else {
                    const content = `${viewKeyDefinition.label} ${failsValidationText}: ${displayValue}`;
                    if (validationMap[key]?.content !== content) {
                        validationMessageFn({ [key]: { type: 'error', content } });
                    }
                }
            }
        }
        else if (displayValue === undefined && required && !hidden) {
            validationMessageFn({
                [viewKeyDefinition.key]: { type: 'required', content: `${viewKeyDefinition.label} is required${debug ? ` ${displayValue}` : ''}` },
            });
        }
        else {
            validationMessageFn({ [key]: undefined });
        }
    }, [displayValue]);
    const formattedDisplayValue = valueFormatter && displayValue !== undefined ? valueFormatter(displayValue) : displayValue;
    return typeof displayValue === 'string' ? _jsx(Typography, { children: formattedDisplayValue }) : _jsx(_Fragment, {});
};
/** @deprecated unused */
const GridCellBooleanChecker = ({ gridRenderCellParams }) => {
    const { field, id, value: originalValue } = gridRenderCellParams;
    return (_jsx(Checkbox, { checked: originalValue?.v === true }));
};
const GridCellNumber = ({ gridRenderCellParams, viewKeyDefinition, validationMessageFn, validationMap }) => {
    const debug = process.env.REACT_APP_DEBUG && false;
    const { precision, valueGetter, limits, key, required, valueFormatter, hidden } = viewKeyDefinition;
    const { field, id, value: originalValue } = gridRenderCellParams;
    let displayValue = typeof originalValue?.v === 'number' ? originalValue.v : typeof originalValue?.v === 'string' ? parseFloat(originalValue.v) : undefined;
    if (valueGetter) {
        displayValue = valueGetter(displayValue, undefined);
    }
    if (typeof precision === 'number' && displayValue) {
        displayValue = roundDecimals(displayValue, precision);
    }
    useEffect(() => {
        if (isNumber(displayValue)) {
            if (limits) {
                if (displayValue !== undefined && isInLimits(displayValue, limits)) {
                    debug && console.log('is in limits', { displayValue, limits });
                    if (validationMap[key]) {
                        validationMessageFn({ [key]: undefined });
                    }
                }
                else {
                    const { min, max } = limits;
                    const content = `${viewKeyDefinition.label} has limits: ${isNonNaNNumber(min) ? `min: ${min}` : ''} ${isNonNaNNumber(max) ? `max: ${max}` : ''}`;
                    if (validationMap[key]?.content !== content) {
                        validationMessageFn({ [key]: { type: 'error', content } });
                    }
                }
            }
        }
        else if (required && !hidden) {
            validationMessageFn({
                [viewKeyDefinition.key]: { type: 'required', content: `${viewKeyDefinition.label} is required${debug ? ` ${displayValue}` : ''}` },
            });
        }
        else {
            validationMessageFn({ [key]: undefined });
        }
    }, [displayValue]);
    const formattedDisplayValue = valueFormatter && displayValue !== undefined ? valueFormatter(displayValue) : displayValue;
    return _jsx(Typography, { children: formattedDisplayValue });
};
const GridCellString = ({ viewKeyDefinition, gridRenderCellParams, validationMessageFn, validationMap }) => {
    const debug = process.env.REACT_APP_DEBUG && false;
    const { field, id, value: originalValue } = gridRenderCellParams;
    const displayValue = typeof originalValue?.v === 'string' ? originalValue.v : undefined;
    const { validationRx, pass, failsValidationText, key, required, valueGetter, valueFormatter, hidden } = viewKeyDefinition;
    debug && console.log(`GridCellString VIEW for ${id} has`, { required, displayValue });
    useEffect(() => {
        if (displayValue && displayValue.length) {
            if (validByRegex(displayValue, validationRx, pass)) {
                debug && console.log('GridCellString valid by regex (View)', { displayValue, validationRx, pass });
                validationMessageFn({ [key]: undefined });
            }
            else {
                const content = `${debug ? `[${viewKeyDefinition.label}] ` : ''}${failsValidationText}: ${displayValue}`;
                if (validationMap[key]?.content !== content) {
                    debug &&
                        console.log('GridCellString NOT valid by regex', {
                            displayValue,
                            validationRx,
                            pass,
                        });
                    validationMessageFn({ [key]: { type: 'error', content } });
                }
            }
        }
        else if (required && !hidden) {
            validationMessageFn({ [key]: { type: 'required', content: `${viewKeyDefinition.label} is required` } });
        }
    }, [displayValue]);
    let formattedDisplayValue = valueGetter ? valueGetter(displayValue, undefined) : displayValue;
    formattedDisplayValue = valueFormatter && formattedDisplayValue !== undefined ? valueFormatter(displayValue) : formattedDisplayValue;
    return _jsx(Typography, { children: formattedDisplayValue });
};
const GridCellDate = ({ gridRenderCellParams, viewKeyDefinition }) => {
    const debug = process.env.REACT_APP_DEBUG && false;
    const { precision, valueGetter } = viewKeyDefinition;
    const { field, id, value: originalValue } = gridRenderCellParams;
    const transformedNumber = valueGetter ? valueGetter(originalValue.v, undefined) : originalValue.v;
    const displayValue = isNonNaNNumber(transformedNumber) ? new Date(transformedNumber) : undefined;
    debug && console.log(`GridCellDate View ${id} has `, { valueGetter, originalValue, transformedNumber, displayValue });
    const importFormatMap = {
        [UserAttributeKeyDatePrecision.Date]: { year: 'numeric', month: 'short', day: '2-digit' },
        [UserAttributeKeyDatePrecision.DateTimeMins]: { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' },
        [UserAttributeKeyDatePrecision.DateTimeSecs]: {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        },
    };
    const format = precision && importFormatMap[precision]
        ? importFormatMap[precision]
        : importFormatMap[UserAttributeKeyDatePrecision.DateTimeMins];
    const displayString = displayValue ? new Date(displayValue).toLocaleDateString(config.locale, format) : '-';
    return _jsx(Typography, { children: displayString });
};
